import React, { Fragment } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSave,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { TermTemplatesCreateProps } from "../../types/termTemplates";
import Editor from "./Editor";

/* --- Styled Components --- */

// 전체를 감싸는 카드 컨테이너
const FormContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

// 헤더: 목록 돌아가기 버튼 영역
const FormHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f1f5f9;
  background-color: #f8fafc;

  button {
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: none;
    color: #64748b;
    font-weight: 600;
    font-size: 0.95rem;
    transition: color 0.2s;
    cursor: pointer;

    &:hover {
      color: #044dac;
    }
  }
`;

// 폼 입력 영역
const FormContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

// 입력 행(Row) 단위
const InputGroup = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;

  .label-area {
    flex: 0 0 160px;
    padding-top: 10px;

    label {
      font-weight: 700;
      color: #334155;
      font-size: 1rem;
      display: flex;
      align-items: center;
      gap: 4px;

      .required {
        color: #ef4444;
      }
    }
  }

  .input-area {
    flex: 1;
    width: 100%;

    input {
      width: 100%;
      padding: 1rem 1rem;
      font-size: 0.95rem;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      transition: all 0.2s;

      &:focus {
        outline: none;
        border-color: #044dac;
        box-shadow: 0 0 0 3px rgba(4, 77, 172, 0.1);
      }

      &::placeholder {
        color: #94a3b8;
      }
    }

    /* 에디터 내부 여백 조정 */
    .quill {
      border-radius: 8px;
      background: #fff;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;

    .label-area {
      flex: none;
      padding-top: 0;
    }
  }
`;

// 하단 버튼 바
const ActionFooter = styled.div`
  margin-top: 2rem;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 12px 12px;

  button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 700;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    border: none;

    svg {
      font-size: 1rem;
    }
  }

  .btn-cancel {
    background-color: #dc343f;
    color: #ffffff;
  }

  .btn-save {
    background: #044dac;
    color: #ffffff;
    box-shadow: 0 4px 6px rgba(4, 77, 172, 0.2);

    &:active {
      transform: translateY(0);
    }
  }
`;

const TermTemplatesCreate = ({
  termTemplatesItem,
  editorRef,
  handleChange,
  handleCreateTermTempates,
  handleGoBack,
}: TermTemplatesCreateProps) => {
  return (
    <Fragment>
      <FormContainer>
        {/* 헤더 부분 */}
        <FormHeader>
          <button onClick={handleGoBack}>
            <FontAwesomeIcon
              icon={faArrowLeft}
              style={{ paddingRight: "3px" }}
            />
            <span>약관 목록으로 돌아가기</span>
          </button>
        </FormHeader>

        {/* 본문 입력 부분 */}
        <FormContent>
          <InputGroup>
            <div className="label-area">
              <label>
                약관 제목 <span className="required">*</span>
              </label>
            </div>
            <div className="input-area">
              <input
                placeholder="관리용 약관 제목을 입력하세요."
                value={termTemplatesItem.title}
                name="title"
                onChange={handleChange}
                autoComplete="off"
              />
            </div>
          </InputGroup>

          <InputGroup>
            <div className="label-area">
              <label>
                약관 언어 <span className="required">*</span>
              </label>
            </div>
            <div className="input-area">
              <select
                className="card-text-select"
                value={termTemplatesItem.language}
                name="language"
                onChange={handleChange}
              >
                <option value="ko">한국어 (KO)</option>
                <option value="en">English (EN)</option>
                <option value="ja">日本語 (JA)</option>
                <option value="zh">中文 (ZH)</option>
              </select>
            </div>
          </InputGroup>

          <InputGroup>
            <div className="label-area">
              <label>
                약관 내용 <span className="required">*</span>
              </label>
            </div>
            <div className="input-area">
              <Editor
                editorRef={editorRef}
                value={termTemplatesItem.content}
                onChange={(content: string) => handleChange(content, "content")}
                hasImage
                resizable
              />
            </div>
          </InputGroup>
        </FormContent>

        {/* 하단 버튼 부분 */}
        <ActionFooter>
          <button className="btn-cancel" onClick={handleGoBack}>
            <FontAwesomeIcon icon={faTimesCircle} />
            취소
          </button>
          <button className="btn-save" onClick={handleCreateTermTempates}>
            <FontAwesomeIcon icon={faSave} />
            약관 저장하기
          </button>
        </ActionFooter>
      </FormContainer>
    </Fragment>
  );
};

export default TermTemplatesCreate;
